import { matchRoutes } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { EXCLUDED_ROUTES, ROUTE_TEMPLATES } from "./routes";

/**
 * The page-view beacon (task #3068; backend docs/usage-stats.md).
 *
 * Sends { route, platform } and nothing else: the route TEMPLATE the screen
 * matched and "web" / "android" / "ios". No user id, no token, no address, no
 * query string, no content. No cookie or storage is read or written, and the
 * request carries no credential. Nothing goes anywhere but our own backend.
 */

export const UNMATCHED = "(unmatched)";

const ALL = [...ROUTE_TEMPLATES, ...EXCLUDED_ROUTES].map((path) => ({ path }));
const EXCLUDED = new Set(EXCLUDED_ROUTES);

/**
 * The template for an address, `null` for a route that sends no beacon, or
 * UNMATCHED. Uses react-router's own ranking, so "/Courses/:slug/answers"
 * wins over "/Courses/:slug/:unitKey" exactly as it does in the app.
 */
export function routeTemplateFor(pathname: string): string | null {
  const matches = matchRoutes(ALL, pathname);
  const path = matches?.[matches.length - 1]?.route.path;
  if (!path) return UNMATCHED;
  return EXCLUDED.has(path) ? null : path;
}

/** True when the browser asks not to be tracked (GPC or Do Not Track). */
export function optedOut(
  nav: Navigator | undefined = globalThis.navigator
): boolean {
  if (!nav) return true;
  const n = nav as Navigator & { globalPrivacyControl?: boolean };
  return n.globalPrivacyControl === true || n.doNotTrack === "1";
}

export function platform(): "web" | "android" | "ios" {
  const p = Capacitor.getPlatform();
  return p === "android" || p === "ios" ? p : "web";
}

/** Build the exact body sent, or null when nothing should be sent. */
export function beaconBody(pathname: string): string | null {
  const route = routeTemplateFor(pathname);
  if (route === null) return null;
  return JSON.stringify({ route, platform: platform() });
}

/**
 * Fire and forget. text/plain so the browser sends it as a simple request
 * (no CORS preflight), credentials omitted, keepalive so a view just before
 * the tab closes still lands. A failure is swallowed: stats must never
 * interrupt anyone.
 */
export function sendPageView(
  pathname: string,
  apiUrl: string | undefined = process.env.REACT_APP_API_URL,
  doFetch: typeof fetch = globalThis.fetch
): boolean {
  if (!apiUrl || !doFetch || optedOut()) return false;
  const body = beaconBody(pathname);
  if (!body) return false;
  try {
    void doFetch(`${apiUrl}/stats/pv`, {
      method: "POST",
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
      body,
      credentials: "omit",
      keepalive: true,
      cache: "no-store",
      referrerPolicy: "no-referrer",
    }).catch(() => {
      /* best effort */
    });
    return true;
  } catch {
    return false;
  }
}
